import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import React, { useContext, useRef, useState } from 'react';

import { useForm, UseFormReturn } from 'react-hook-form';

import { useNavigate } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { CallCenterCreateDTO, getCallCenterCreateSchema } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';

import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';

import { CallCenterForm, CallCenterFormValues } from './CallCenterForm';

type CallCenterCreateProps = {
  authUser: AuthUser | undefined;
  isFetchingFromProps?: boolean;
};

export const CallCenterCreate = (props: CallCenterCreateProps) => {
  const { authUser, isFetchingFromProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const api = useContext(ApiContext);

  const navigate = useNavigate();

  const callCenterCreateMutation = api.callCenters.useCreate();

  const callCenterCreateFormContext = useForm<CallCenterCreateDTO>({
    resolver: (values, context, options) => {
      const schema = getCallCenterCreateSchema();
      const resolver = zodResolver(schema);
      return resolver(values, context, options);
    },
    defaultValues: {
      escalationEmails: [],
      callDroppedEmails: [],
      phoneCallWebhookEnabled: false
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  const handleSave = apiSubmitHandler(
    callCenterCreateFormContext.handleSubmit,
    callCenterCreateFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const callCenterDetail = await callCenterCreateMutation.create({ data });
        enqueueSnackbar('Call Center created');
        navigate(`${getFullAppRoutePath(appRoutePaths.CallCenter)}/${callCenterDetail.id}`, { replace: true });
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  const isFetchingOrPending = isFetchingFromProps || callCenterCreateMutation.isPending;

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <BackdropSpinner open={isFetchingOrPending} />
      <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
        {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
        {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
        {authUser && (
          <CallCenterForm
            callCenterFormContext={callCenterCreateFormContext as UseFormReturn<CallCenterFormValues>}
            authUser={authUser}
          />
        )}
      </Stack>
      {isOverflowing && <Divider />}
      <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
        <Button onClick={handleSave} text='Save' variant='contained' size='small' sx={{ textTransform: 'none' }} />
      </FlexBox>
    </FlexBox>
  );
};
